// Deliberately 1:1, no separate conversion factor: SWMM's own [COORDINATES]
// never declares a unit of its own, so the coordinate-plane distance IS
// taken as the length value directly.

import { isGeographicCrs } from "../geo/crs.ts";
import { haversineDistanceMeters, toLonLat } from "../geo/reproject.ts";
import { metersToProjectLength } from "../units.ts";
import type { Point, SwmmProject } from "./types.ts";
import { getCaseInsensitive } from "./idLookup.ts";

function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** 2 decimals — a raw double like "5378.441100913908" isn't a precision anyone would specify a pipe length to by hand. */
function roundLength(value: number): number {
  return Math.round(value * 100) / 100;
}

/** A GEOGRAPHIC crs's raw coordinates are degrees, where a plain coordinate-plane distance is meaningless (a fractional-degree number, not a pipe length) — that case reprojects to lon/lat first and measures a real great-circle distance instead. */
export function drawnConduitLength(
  from: Point,
  to: Point,
  crs: string | null,
  flowUnits: string
): number {
  if (crs && isGeographicCrs(crs)) {
    const meters = haversineDistanceMeters(toLonLat(from, crs), toLonLat(to, crs));
    return roundLength(metersToProjectLength(meters, flowUnits));
  }
  return roundLength(distance(from, to));
}

/** A link whose either end node has no resolvable [COORDINATES] position is simply omitted, not zeroed out. */
export function computeConduitLengths(project: SwmmProject): Map<string, number> {
  const lengths = new Map<string, number>();
  for (const link of project.links) {
    if (link.kind !== "conduit") continue;
    // A [COORDINATES] row's own name can legally differ in case from the
    // node section row it belongs to.
    const from = getCaseInsensitive(project.coordinates, link.fromNode);
    const to = getCaseInsensitive(project.coordinates, link.toNode);
    if (!from || !to) continue;
    const vertices = project.vertices.get(link.name) ?? [];
    const path = [from, ...vertices, to];
    let total = 0;
    for (let i = 1; i < path.length; i++) total += distance(path[i - 1], path[i]);
    lengths.set(link.name, total);
  }
  return lengths;
}

/** A subset of `computeConduitLengths`'s own output is fine — every conduit not present in `lengths` is left untouched. */
export function writeConduitLengths(
  raw: ReadonlyMap<string, string[][]>,
  lengths: Map<string, number>
): Map<string, string[][]> {
  const rows = (raw.get("CONDUITS") ?? []).map((row) => {
    const length = lengths.get(row[0]);
    if (length === undefined) return row;
    const next = [...row];
    next[3] = String(length);
    return next;
  });
  const next = new Map(raw);
  next.set("CONDUITS", rows);
  return next;
}
